import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { ListItem } from '@/components/ui/ListItem';
import { OnboardingLayout } from '../Layout';
import { useAddPaymentMethod, useDeletePaymentMethod, usePaymentMethods } from '@/api/queries/payments';
import { paths } from '@/routes/paths';
import { Spinner } from '@/components/ui/Spinner';
import { IcTrash, IcPlus } from '@/components/ui/icons';
import { useUIStore } from '@/store/ui';
import { useState } from 'react';
import { Input } from '@/components/ui/Input';
import { BottomSheet } from '@/components/ui/BottomSheet';

export function OnboardingPaymentCardsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pms = usePaymentMethods();
  const add = useAddPaymentMethod();
  const del = useDeletePaymentMethod();
  const pushToast = useUIStore((s) => s.pushToast);

  const [open, setOpen] = useState(false);
  const [last4, setLast4] = useState('');
  const [mm, setMm] = useState('');
  const [yy, setYy] = useState('');

  const handleAdd = async () => {
    if (!/^\d{4}$/.test(last4)) {
      pushToast({ body: 'Card number last 4 invalid', variant: 'error' });
      return;
    }
    try {
      await add.mutateAsync({
        brand: 'visa',
        last4,
        expiry_month: Number(mm),
        expiry_year: 2000 + Number(yy),
      });
      setOpen(false);
      setLast4('');
      setMm('');
      setYy('');
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  return (
    <OnboardingLayout back title={t('onboarding.payment.list_title')}>
      {pms.isLoading && <Spinner />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pms.data?.map((p) => (
          <ListItem
            key={p.id}
            title={`${p.brand.toUpperCase()} •••• ${p.last4}`}
            subtitle={`${String(p.expiry_month).padStart(2, '0')} / ${p.expiry_year}`}
            trailing={
              <button onClick={() => del.mutate(p.id)} aria-label="Delete" style={{ color: 'var(--color-danger)' }}>
                <IcTrash />
              </button>
            }
          />
        ))}
      </div>
      <Button block variant="inverted" startIcon={<IcPlus />} onClick={() => setOpen(true)}>
        {t('onboarding.payment.add_card')}
      </Button>
      <Button block size="lg" variant="ghost" onClick={() => navigate(paths.onboardingEvacuation)}>
        {t('common.continue')}
      </Button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={t('subscribe.card_form.title')}>
        <Input
          label={t('subscribe.card_form.number')}
          placeholder="•••• •••• •••• 1234"
          value={last4}
          onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(-4))}
          inputMode="numeric"
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label="MM"
            placeholder="MM"
            inputMode="numeric"
            value={mm}
            onChange={(e) => setMm(e.target.value.replace(/\D/g, '').slice(0, 2))}
          />
          <Input
            label="YY"
            placeholder="YY"
            inputMode="numeric"
            value={yy}
            onChange={(e) => setYy(e.target.value.replace(/\D/g, '').slice(0, 2))}
          />
        </div>
        <Button block size="lg" variant="inverted" onClick={handleAdd} loading={add.isPending}>
          {t('subscribe.card_form.save')}
        </Button>
      </BottomSheet>
    </OnboardingLayout>
  );
}
